<template>
  <dialog
    ref="dialog"
    class="staff-editor-dialog public-booking-block-dialog"
    aria-labelledby="public-booking-block-title"
    aria-describedby="public-booking-block-description"
    :aria-busy="saving"
    @cancel.prevent="requestClose"
  >
    <header class="staff-editor-header">
      <span class="material-icons staff-editor-heading-icon" aria-hidden="true">block</span>
      <div>
        <h2 id="public-booking-block-title">Aggiungi blocco</h2>
        <p id="public-booking-block-description">Chiudi una fascia oraria alle prenotazioni manuali e online.</p>
      </div>
      <button type="button" class="staff-editor-close" aria-label="Chiudi aggiunta blocco" :disabled="saving" @click="requestClose">
        <span class="material-icons" aria-hidden="true">close</span>
      </button>
    </header>

    <form class="staff-editor-form" @submit.prevent="submit">
      <div class="staff-editor-body">
        <fieldset class="staff-editor-section" :disabled="saving">
          <legend>Giorno e orari</legend>
          <div class="staff-editor-grid">
            <label class="staff-editor-field staff-editor-field--wide">
              <span>Data</span>
              <input v-model="dateKey" type="date" required @input="fieldsChanged" />
            </label>
            <label class="staff-editor-field">
              <span>Dalle</span>
              <input v-model="start" type="time" step="60" required @input="fieldsChanged" />
            </label>
            <label class="staff-editor-field">
              <span>Alle</span>
              <input v-model="end" type="time" step="60" required @input="fieldsChanged" />
            </label>
            <label class="staff-editor-field staff-editor-field--wide">
              <span>Nota <small>(facoltativa)</small></span>
              <textarea v-model="note" maxlength="1000" rows="3" placeholder="Es. riunione, studio chiuso…" @input="fieldsChanged"></textarea>
              <small>La nota sarà visibile solo allo staff.</small>
            </label>
          </div>
        </fieldset>

        <p class="public-booking-block-info">
          Gli orari che si sovrappongono al blocco non saranno disponibili per nuove prenotazioni.
        </p>

        <section v-if="showConflicts" class="public-booking-block-warning" role="alert" aria-labelledby="public-booking-block-warning-title">
          <h3 id="public-booking-block-warning-title">
            <span class="material-icons" aria-hidden="true">warning_amber</span>
            {{ conflicts.length === 1 ? 'C’è già una prenotazione in questa fascia' : `Ci sono già ${conflicts.length} prenotazioni in questa fascia` }}
          </h3>
          <ul>
            <li v-for="(booking, index) in conflicts" :key="booking.id || index">
              <strong>{{ booking.timeKey || booking.time || 'Orario da verificare' }}</strong>
              <span>{{ conflictName(booking) }}</span>
            </li>
          </ul>
          <p>Le prenotazioni già fissate resteranno confermate. Creando il blocco impedirai solo nuove prenotazioni in questa fascia.</p>
        </section>

        <p v-if="validationError || error" class="public-booking-block-error" role="alert">{{ validationError || error }}</p>
      </div>

      <footer class="staff-editor-footer">
        <p>Il blocco sarà visibile nella tabella delle prenotazioni.</p>
        <div class="staff-editor-actions">
          <button type="button" class="staff-editor-button staff-editor-cancel" :disabled="saving" @click="requestClose">Annulla</button>
          <button type="submit" class="staff-editor-button staff-editor-save" :disabled="saving">
            <span class="material-icons" aria-hidden="true">block</span>
            {{ saving ? 'Salvataggio…' : (showConflicts ? 'Crea blocco comunque' : 'Crea blocco') }}
          </button>
        </div>
      </footer>
    </form>
  </dialog>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

function todayInRome() {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const value = (type) => parts.find((part) => part.type === type)?.value;
  return `${value('year')}-${value('month')}-${value('day')}`;
}

export default {
  name: 'PublicBookingBlockDialog',
  props: {
    open: { type: Boolean, default: false },
    initialDate: { type: String, default: '' },
    saving: { type: Boolean, default: false },
    error: { type: String, default: '' },
    conflicts: { type: Array, default: () => [] },
  },
  emits: ['close', 'submit', 'change'],
  setup(props, { emit }) {
    const dialog = ref(null);
    const dateKey = ref('');
    const start = ref('');
    const end = ref('');
    const note = ref('');
    const validationError = ref('');
    const checkedSignature = ref('');
    const formSignature = computed(() => JSON.stringify([dateKey.value, start.value, end.value, note.value.trim()]));
    const showConflicts = computed(() => props.conflicts.length > 0 && checkedSignature.value === formSignature.value);

    const resetForm = () => {
      dateKey.value = props.initialDate || todayInRome();
      start.value = '';
      end.value = '';
      note.value = '';
      validationError.value = '';
      checkedSignature.value = '';
    };
    const syncDialog = async () => {
      await nextTick();
      if (!dialog.value) return;
      if (props.open && !dialog.value.open) dialog.value.showModal();
      else if (!props.open && dialog.value.open) dialog.value.close();
    };
    watch(() => props.open, (open) => {
      if (open) resetForm();
      syncDialog();
    });
    onMounted(() => {
      if (props.open) resetForm();
      syncDialog();
    });
    onBeforeUnmount(() => {
      if (dialog.value?.open) dialog.value.close();
    });
    const requestClose = () => {
      if (!props.saving) emit('close');
    };
    const fieldsChanged = () => {
      validationError.value = '';
      checkedSignature.value = '';
      emit('change');
    };
    const submit = () => {
      if (props.saving) return;
      validationError.value = '';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey.value) || !/^\d{2}:\d{2}$/.test(start.value) || !/^\d{2}:\d{2}$/.test(end.value)) {
        validationError.value = 'Inserisci la data e gli orari del blocco.';
        return;
      }
      if (end.value <= start.value) {
        validationError.value = 'L’orario finale deve essere successivo a quello iniziale, nello stesso giorno.';
        return;
      }
      const confirmedConflictIds = showConflicts.value
        ? props.conflicts.map((booking) => String(booking.id || '')).filter(Boolean)
        : [];
      checkedSignature.value = formSignature.value;
      emit('submit', {
        dateKey: dateKey.value, start: start.value, end: end.value,
        note: note.value.trim(), confirmedConflictIds,
      });
    };
    const conflictName = (booking) => booking.fullName
      || [booking.firstName, booking.lastName].filter(Boolean).join(' ')
      || booking.email
      || 'Cliente';

    return { dialog, dateKey, start, end, note, validationError, showConflicts, requestClose, fieldsChanged, submit, conflictName };
  },
};
</script>

<style scoped src="../../assets/css/staff-editor-dialog.css"></style>
<style scoped>
.public-booking-block-info { margin: 0; color: #666; font-size: 13px; line-height: 1.6; }
.public-booking-block-warning { min-width: 0; padding: 14px; border: 1px solid #e7bd79; border-radius: 12px; background: #fff7e8; color: #79440d; }
.public-booking-block-warning h3 { display: flex; align-items: flex-start; gap: 8px; margin: 0; color: inherit; font-size: 14px; font-weight: 700; line-height: 1.5; text-align: left; }
.public-booking-block-warning h3 .material-icons { flex-shrink: 0; font-size: 21px; line-height: 1; }
.public-booking-block-warning ul { display: grid; gap: 6px; max-height: 160px; margin: 12px 0; padding: 0; overflow: auto; list-style: none; }
.public-booking-block-warning li { display: flex; align-items: baseline; gap: 10px; min-width: 0; font-size: 13px; }
.public-booking-block-warning li strong { flex-shrink: 0; }
.public-booking-block-warning li span { overflow-wrap: anywhere; }
.public-booking-block-warning p { margin: 0; font-size: 13px; line-height: 1.6; }
.public-booking-block-error { margin: 0; padding: 10px 12px; border: 1px solid #efcccc; border-radius: 8px; background: #fff4f4; color: #a12828; font-size: 13px; line-height: 1.5; }
@media (max-width: 600px) {
  .public-booking-block-dialog .staff-editor-footer > p { font-size: 11px; }
  #app .public-booking-block-dialog .staff-editor-footer .staff-editor-button { min-height: 48px; }
}
</style>
